/**
 * MermaidView — renderiza diagramas mermaid (C4, sequência, flowchart…) que hoje
 * vinham como código cru nos artefatos técnicos.
 *
 * - MermaidBlock: renderiza UM bloco mermaid em SVG; se a sintaxe falhar (mermaid
 *   gerado por IA às vezes tem erros), cai graciosamente para o código.
 * - RichContent: divide um conteúdo markdown em partes de texto e blocos
 *   ```mermaid```; se o conteúdo inteiro parecer mermaid (sem fences), renderiza
 *   como diagrama.
 */
import { C } from '@/theme/palette';
import { useEffect, useState } from 'react';

// Carrega o mermaid SOB DEMANDA (dynamic import) — dep pesada, só necessária
// quando há um diagrama para renderizar. Mantém o bundle principal enxuto.
let _mermaidPromise = null;
function getMermaid() {
  if (!_mermaidPromise) {
    _mermaidPromise = import('mermaid').then((mod) => {
      const mermaid = mod.default ?? mod;
      mermaid.initialize({
        startOnLoad: false,
        theme: 'dark',
        securityLevel: 'strict',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      });
      return mermaid;
    });
  }
  return _mermaidPromise;
}

let _uid = 0;

/**
 * sanitizeMermaid — normaliza um bloco mermaid gerado por IA que tende a quebrar
 * o parser por causa de caracteres especiais (acentos/parênteses/`:`/`;`/`<>` em
 * labels não citados, aspas curvas, espaços invisíveis). Usado como FALLBACK: só
 * roda quando a renderização do código ORIGINAL falhou — então pode ser agressivo
 * sem risco de estragar um diagrama que já era válido.
 */
export function sanitizeMermaid(code) {
  let s = String(code || '');
  // 1) caracteres invisíveis / aspas curvas → equivalentes ASCII que o parser aceita.
  s = s
    .replace(/[“”]/g, '"')          // “ ” → "
    .replace(/[‘’]/g, "'")          // ‘ ’ → '
    .replace(/ /g, ' ')                    // nbsp → espaço
    .replace(/[​-‍﻿]/g, '');     // zero-width
  // 2) flowchart/graph: garante que labels de nó / aresta com caractere especial
  //    fiquem entre aspas (a causa nº 1 de "diagrama não renderizável"). Só
  //    envolve quando NÃO está citado e HÁ caractere não-alfanumérico.
  if (/^\s*(flowchart|graph)\b/m.test(s)) {
    const wrap = (body) => `"${String(body).replace(/"/g, "'").trim()}"`;
    const needs = (body) => {
      const t = String(body).trim();
      if (/^".*"$/.test(t)) return false;              // já citado
      // não-citado E (tem caractere especial OU é a palavra reservada 'end')
      return /[^\w\s]/.test(t) || /^end$/i.test(t);
    };
    // Nós [label] e {label} (match guloso até o fechamento na mesma linha).
    s = s.replace(/\[([^\]\n]+)\]/g, (all, b) => (needs(b) ? `[${wrap(b)}]` : all));
    s = s.replace(/\{([^}\n]+)\}/g, (all, b) => (needs(b) ? `{${wrap(b)}}` : all));
    // Rótulos de aresta -->|label|.
    s = s.replace(/\|([^|\n]+)\|/g, (all, b) => (needs(b) ? `|${wrap(b)}|` : all));
  }
  return s;
}

const PRE_STYLE = {
  margin: 0, padding: '12px 16px', fontFamily: 'ui-monospace, monospace',
  fontSize: 12, lineHeight: 1.6, color: C.neutralDim, whiteSpace: 'pre-wrap',
  wordBreak: 'break-word', background: C.black, borderRadius: 8,
};

export function MermaidBlock({ code }) {
  const [svg, setSvg] = useState('');
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const id = `mmd-${Date.now().toString(36)}-${(_uid += 1)}`;
    getMermaid()
      .then(async (mermaid) => {
        // 1ª tentativa: código original. 2ª: versão sanitizada (só se a 1ª falhar
        // — assim a limpeza agressiva nunca estraga um diagrama já válido).
        try {
          return await mermaid.render(id, code);
        } catch (e1) {
          try {
            return await mermaid.render(`${id}s`, sanitizeMermaid(code));
          } catch (e2) {
            throw new Error((e2 && e2.message) || (e1 && e1.message) || 'erro de render');
          }
        }
      })
      .then((res) => { if (!cancelled) { setSvg(res.svg); setErr(null); } })
      .catch((e) => { if (!cancelled) setErr((e && e.message) || 'erro desconhecido'); });
    return () => { cancelled = true; };
  }, [code]);

  if (err) {
    // Chunk do mermaid obsoleto (novo build trocou o hash) → recarregar resolve.
    const staleChunk = /dynamically imported module|failed to fetch|importing a module script failed|error loading/i.test(err);
    if (staleChunk) {
      return (
        <div style={{ marginTop: 8, padding: 12, border: `1px solid ${C.warn}44`, borderRadius: 8, background: C.black }}>
          <div style={{ fontSize: 12.5, color: C.warn, marginBottom: 8 }}>
            Novo build detectado — recarregue a página para renderizar o diagrama.
          </div>
          <button onClick={() => window.location.reload()}
            style={{ cursor: 'pointer', background: 'transparent', color: C.warn,
              border: `1px solid ${C.warn}`, borderRadius: 7, padding: '6px 12px', fontSize: 12.5, fontWeight: 600 }}>
            ↻ Recarregar página
          </button>
        </div>
      );
    }
    return (
      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 11, color: '#FF8A80', margin: '0 0 4px', whiteSpace: 'pre-wrap' }}>
          diagrama não renderizável — {err}
        </div>
        <pre style={PRE_STYLE}>{code}</pre>
      </div>
    );
  }
  if (!svg) {
    return <div style={{ fontSize: 12, color: C.neutralMuted, padding: 8 }}>Renderizando diagrama…</div>;
  }
  return (
    <div
      style={{ background: C.black, border: `1px solid ${C.blackBorder}`, borderRadius: 8,
        padding: 12, marginTop: 8, overflow: 'auto', textAlign: 'center' }}
      // svg vem sanitizado pelo mermaid (securityLevel: 'strict')
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

// ─── D2 (Terrastruct) — motor de diagramas padrão para novas gerações ────────
//
// Migração Mermaid → D2: o D2 tem sintaxe mais tolerante, layout melhor e
// sequência/C4 nativos. Carregado SOB DEMANDA (o build browser é ~8 MB com o
// WASM embutido em base64 + worker via blob — nada externo p/ o Vite/nginx
// resolverem). Diagramas ```d2``` usam este renderer; ```mermaid``` legados
// continuam no MermaidBlock.
let _d2Promise = null;
function getD2() {
  if (!_d2Promise) {
    _d2Promise = import('@terrastruct/d2').then((mod) => {
      const D2 = mod.D2 ?? mod.default?.D2 ?? mod.default;
      return new D2();
    });
  }
  return _d2Promise;
}

/**
 * sanitizeD2 — normaliza um bloco D2 gerado por IA que tende a quebrar por (a) `$`
 * solto — o D2 lê `$` como início de substituição `${var}` ("substitutions must
 * begin on {") mesmo dentro de aspas — e (b) rótulos NÃO citados com caractere
 * especial (ex.: `cliente: Cliente/Frontend` → "unexpected text after unquoted
 * string"). FALLBACK: só roda quando o compile do ORIGINAL falhou — então pode
 * corrigir agressivamente sem estragar um diagrama já válido. Conservador: não toca
 * em blocos `{`, keywords (shape/direction/…) nem em valores já entre aspas.
 */
export function sanitizeD2(code) {
  const SPECIAL = /[/()&|<>@#,;%!?$]/;   // caracteres que quebram rótulo D2 não citado ($ inclui)
  const KEYWORD = /^(shape|direction|style|near|icon|width|height|label|constraint|source-arrowhead|target-arrowhead|grid-rows|grid-columns|class|link|tooltip)\b/i;
  const quote = (v) => `"${String(v).replace(/"/g, "'").trim()}"`;
  return String(code || '')
    .replace(/[“”]/g, '"').replace(/[‘’]/g, "'")
    // Escapa `$` solto (não já escapado e não seguido de `{`) → `\$`, evitando que o
    // D2 o interprete como substituição. Ex.: "Preço $100" → "Preço \$100".
    .replace(/(?<!\\)\$(?!\{)/g, '\\$')
    .split('\n')
    .map((line) => {
      const t = line.replace(/\s+$/, '');
      // pula vazio/comentário e ABERTURA/fechamento de bloco (chaves) — mas NÃO
      // linhas que terminam em ']' (pode ser texto de rótulo, ex.: "... [nota]").
      if (!t || t.trim().startsWith('#') || /\{\s*$/.test(t) || /^\s*[}\])]\s*$/.test(t)) return line;
      // rótulo de conexão: `a -> b: rótulo`  (cita o rótulo após o ':')
      const conn = t.match(/^(\s*.+?(?:->|<->|--|<-).+?:\s*)(.+)$/);
      if (conn) {
        const v = conn[2].trim();
        return SPECIAL.test(v) && !/^".*"$/.test(v) ? `${conn[1]}${quote(v)}` : line;
      }
      // atribuição `chave: valor` (cita o valor se tiver especial e não for keyword/bloco)
      const kv = t.match(/^(\s*[\w.\-]+:\s*)(.+)$/);
      if (kv && !KEYWORD.test(kv[1].trim())) {
        const v = kv[2].trim();
        if (SPECIAL.test(v) && !/^".*"$/.test(v) && !/[{[]$/.test(v)) return `${kv[1]}${quote(v)}`;
      }
      return line;
    })
    .join('\n');
}

// Serializa as renderizações: o D2 roda num único worker (message passing); com
// vários diagramas na mesma página, chamadas concorrentes poderiam se misturar.
let _d2Chain = Promise.resolve();
let _d2Salt = 0;
async function _renderD2Once(d2, code, salt) {
  // themeID 200 = tema escuro (combina com a UI); noXMLTag p/ embutir direto no
  // HTML; salt garante IDs únicos quando há vários diagramas no mesmo documento.
  const opts = { layout: 'dagre', themeID: 200, sketch: false, pad: 12, center: true, noXMLTag: true, salt };
  const result = await d2.compile(String(code || ''), opts);
  return d2.render(result.diagram, { ...(result.renderOptions || {}), themeID: 200, noXMLTag: true, salt });
}
function renderD2(code) {
  const salt = `d2-${(_d2Salt += 1)}`;
  const run = async () => {
    const d2 = await getD2();
    // 1ª tentativa: código original. 2ª: versão sanitizada (só se a 1ª falhar —
    // assim a limpeza agressiva nunca estraga um diagrama já válido).
    try {
      return await _renderD2Once(d2, code, salt);
    } catch (e1) {
      try {
        return await _renderD2Once(d2, sanitizeD2(code), `${salt}s`);
      } catch (e2) {
        throw new Error((e2 && e2.message) || (e1 && e1.message) || 'erro de render');
      }
    }
  };
  const p = _d2Chain.then(run, run);
  _d2Chain = p.catch(() => {}); // mantém a fila viva mesmo se um render falhar
  return p;
}

export function D2Block({ code }) {
  const [svg, setSvg] = useState('');
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setSvg(''); setErr(null);
    renderD2(code)
      .then((res) => { if (!cancelled) { setSvg(res); setErr(null); } })
      .catch((e) => { if (!cancelled) setErr((e && e.message) || 'erro de render'); });
    return () => { cancelled = true; };
  }, [code]);

  if (err) {
    const staleChunk = /dynamically imported module|failed to fetch|importing a module script failed|error loading/i.test(err);
    if (staleChunk) {
      return (
        <div style={{ marginTop: 8, padding: 12, border: `1px solid ${C.warn}44`, borderRadius: 8, background: C.black }}>
          <div style={{ fontSize: 12.5, color: C.warn, marginBottom: 8 }}>
            Novo build detectado — recarregue a página para renderizar o diagrama.
          </div>
          <button onClick={() => window.location.reload()}
            style={{ cursor: 'pointer', background: 'transparent', color: C.warn,
              border: `1px solid ${C.warn}`, borderRadius: 7, padding: '6px 12px', fontSize: 12.5, fontWeight: 600 }}>
            ↻ Recarregar página
          </button>
        </div>
      );
    }
    return (
      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 11, color: '#FF8A80', margin: '0 0 4px', whiteSpace: 'pre-wrap' }}>
          diagrama não renderizável — {err}
        </div>
        <pre style={PRE_STYLE}>{code}</pre>
      </div>
    );
  }
  if (!svg) {
    return <div style={{ fontSize: 12, color: C.neutralMuted, padding: 8 }}>Renderizando diagrama…</div>;
  }
  return (
    <div
      style={{ background: C.black, border: `1px solid ${C.blackBorder}`, borderRadius: 8,
        padding: 12, marginTop: 8, overflow: 'auto', textAlign: 'center' }}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

const MERMAID_START = /^(sequenceDiagram|flowchart|graph|classDiagram|erDiagram|stateDiagram(-v2)?|journey|gantt|pie|mindmap|timeline|C4Context|C4Container|C4Component|C4Dynamic)\b/;

/** Divide um conteúdo em partes de texto e blocos de diagrama (```d2``` ou ```mermaid```). */
export function splitMermaid(content) {
  const text = String(content || '');
  const parts = [];
  const fence = /```(d2|mermaid)\s*\n([\s\S]*?)```/g;
  let last = 0;
  let m;
  while ((m = fence.exec(text)) !== null) {
    if (m.index > last) parts.push({ type: 'text', value: text.slice(last, m.index) });
    parts.push({ type: m[1] === 'd2' ? 'd2' : 'mermaid', value: m[2].trim() });
    last = fence.lastIndex;
  }
  if (last < text.length) parts.push({ type: 'text', value: text.slice(last) });
  // Sem fences, mas o conteúdo inteiro parece um diagrama mermaid (legado).
  if (parts.length <= 1 && MERMAID_START.test(text.trim())) {
    return [{ type: 'mermaid', value: text.trim() }];
  }
  return parts;
}

// ─── Renderizador Markdown (dependency-free, XSS-safe) ───────────────────────
//
// Converte os TRECHOS DE TEXTO markdown dos artefatos (relatórios de análise,
// artefatos técnicos) em elementos React — nunca em HTML cru — então não há
// superfície de XSS mesmo com conteúdo vindo da IA/RAG. Cobre o que os artefatos
// usam: títulos, tabelas, listas, **negrito**, *itálico*, `code`, links, citação,
// `---` e code fences. O que não casar cai como parágrafo (texto preservado).
// Diagramas ```merma``` já são extraídos antes por splitMermaid.

const MD = {
  h: { color: '#E8E8E0', fontWeight: 700, lineHeight: 1.3, margin: '14px 0 8px' },
  p: { color: '#C8C8C0', fontSize: 13, lineHeight: 1.65, margin: '0 0 10px' },
  code: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12,
    background: C.black, border: `1px solid ${C.blackBorder}`, borderRadius: 4,
    padding: '1px 5px', color: '#E6C07B',
  },
  link: { color: '#6BA5FF', textDecoration: 'underline' },
  li: { color: '#C8C8C0', fontSize: 13, lineHeight: 1.6, margin: '3px 0' },
  quote: {
    borderLeft: '3px solid #3A3A3A', margin: '0 0 10px', padding: '2px 0 2px 12px',
    color: C.neutralDim, fontStyle: 'italic',
  },
  hr: { border: 0, borderTop: `1px solid ${C.blackBorder}`, margin: '16px 0' },
  tableWrap: { overflowX: 'auto', margin: '0 0 12px' },
  table: { borderCollapse: 'collapse', width: '100%', fontSize: 12.5 },
  th: {
    border: `1px solid ${C.blackBorder}`, padding: '6px 10px', textAlign: 'left',
    background: C.blackElev, color: '#E8E8E0', fontWeight: 600, whiteSpace: 'nowrap',
  },
  td: { border: `1px solid ${C.blackBorder}`, padding: '6px 10px', color: '#C8C8C0', verticalAlign: 'top' },
};
const H_SIZE = { 1: 20, 2: 17, 3: 15, 4: 14, 5: 13, 6: 12.5 };

/** href só é aceito se for http(s)/mailto/relativo/âncora — bloqueia javascript:/data:. */
function safeHref(url) {
  const u = String(url || '').trim();
  return /^(https?:|mailto:|#|\/|\.\/|\.\.\/)/i.test(u) ? u : null;
}

/** Inline: negrito, itálico, code e links [texto](url). Retorna nós React (sem HTML cru). */
function renderInline(text, keyPrefix) {
  const out = [];
  let rest = String(text ?? '');
  let k = 0;
  const RX = /(\*\*([^*]+)\*\*)|(`([^`]+)`)|(\[([^\]]+)\]\(([^)\s]+)\))|(\*([^*\s][^*]*?)\*)|(_([^_\s][^_]*?)_)/;
  while (rest) {
    const m = RX.exec(rest);
    if (!m) { out.push(rest); break; }
    if (m.index > 0) out.push(rest.slice(0, m.index));
    const key = `${keyPrefix}-x${k++}`;
    if (m[1]) out.push(<strong key={key}>{m[2]}</strong>);
    else if (m[3]) out.push(<code key={key} style={MD.code}>{m[4]}</code>);
    else if (m[5]) {
      const href = safeHref(m[7]);
      out.push(href
        ? <a key={key} href={href} target="_blank" rel="noopener noreferrer" style={MD.link}>{m[6]}</a>
        : m[6]);
    } else if (m[8]) out.push(<em key={key}>{m[9]}</em>);
    else if (m[10]) out.push(<em key={key}>{m[11]}</em>);
    rest = rest.slice(m.index + m[0].length);
  }
  return out;
}

function splitRow(line) {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

const isBlockStart = (l) =>
  /^\s*#{1,6}\s+/.test(l) || /^\s*```/.test(l) || /^\s*>\s?/.test(l)
  || /^\s*([-*+]|\d+\.)\s+/.test(l) || /^\s*([-*_])\1\1[-*_\s]*$/.test(l);

/** Divide um texto markdown em blocos (heading/table/list/quote/code/hr/para). */
function parseBlocks(text) {
  const lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }

    if (/^\s*```/.test(line)) {                       // code fence
      const lang = line.replace(/^\s*```/, '').trim();
      const buf = []; i++;
      while (i < lines.length && !/^\s*```/.test(lines[i])) { buf.push(lines[i]); i++; }
      i++;
      blocks.push({ type: 'code', lang, value: buf.join('\n') });
      continue;
    }
    const h = /^\s*(#{1,6})\s+(.*)$/.exec(line);      // heading
    if (h) { blocks.push({ type: 'heading', level: h[1].length, value: h[2].trim() }); i++; continue; }

    if (/^\s*([-*_])\1\1[-*_\s]*$/.test(line)) { blocks.push({ type: 'hr' }); i++; continue; }  // hr

    // tabela: linha com | + próxima linha separadora (|---|:--:|)
    if (line.includes('|') && i + 1 < lines.length
        && /-/.test(lines[i + 1]) && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1])) {
      const header = splitRow(line);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].trim() && lines[i].includes('|')) {
        rows.push(splitRow(lines[i])); i++;
      }
      blocks.push({ type: 'table', header, rows });
      continue;
    }
    if (/^\s*>\s?/.test(line)) {                      // blockquote
      const buf = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) { buf.push(lines[i].replace(/^\s*>\s?/, '')); i++; }
      blocks.push({ type: 'quote', value: buf.join(' ') });
      continue;
    }
    if (/^\s*([-*+]|\d+\.)\s+/.test(line)) {          // lista
      const ordered = /^\s*\d+\.\s+/.test(line);
      const items = [];
      while (i < lines.length && /^\s*([-*+]|\d+\.)\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*([-*+]|\d+\.)\s+/, '')); i++;
      }
      blocks.push({ type: 'list', ordered, items });
      continue;
    }
    const buf = [line]; i++;                          // parágrafo
    while (i < lines.length && lines[i].trim() && !isBlockStart(lines[i])) { buf.push(lines[i]); i++; }
    blocks.push({ type: 'para', value: buf.join(' ') });
  }
  return blocks;
}

/** Renderiza um texto markdown como elementos React (sem HTML cru). */
export function Markdown({ text }) {
  const blocks = parseBlocks(text);
  return (
    <div>
      {blocks.map((b, i) => {
        const key = `b${i}`;
        if (b.type === 'heading') {
          const Tag = `h${Math.min(b.level, 6)}`;
          return <Tag key={key} style={{ ...MD.h, fontSize: H_SIZE[b.level] || 13 }}>{renderInline(b.value, key)}</Tag>;
        }
        if (b.type === 'hr') return <hr key={key} style={MD.hr} />;
        if (b.type === 'code') return <pre key={key} style={PRE_STYLE}>{b.value}</pre>;
        if (b.type === 'quote') return <blockquote key={key} style={MD.quote}>{renderInline(b.value, key)}</blockquote>;
        if (b.type === 'list') {
          const Tag = b.ordered ? 'ol' : 'ul';
          return (
            <Tag key={key} style={{ margin: '0 0 10px', paddingLeft: 22 }}>
              {b.items.map((it, j) => <li key={`${key}-${j}`} style={MD.li}>{renderInline(it, `${key}-${j}`)}</li>)}
            </Tag>
          );
        }
        if (b.type === 'table') {
          return (
            <div key={key} style={MD.tableWrap}>
              <table style={MD.table}>
                <thead>
                  <tr>{b.header.map((c, j) => <th key={j} style={MD.th}>{renderInline(c, `${key}-h${j}`)}</th>)}</tr>
                </thead>
                <tbody>
                  {b.rows.map((row, r) => (
                    <tr key={r}>
                      {b.header.map((_, c) => <td key={c} style={MD.td}>{renderInline(row[c] ?? '', `${key}-${r}-${c}`)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        return <p key={key} style={MD.p}>{renderInline(b.value, key)}</p>;
      })}
    </div>
  );
}

/** Renderiza conteúdo markdown com texto + diagramas mermaid intercalados. */
export function RichContent({ content }) {
  const parts = splitMermaid(content);
  return (
    <div>
      {parts.map((p, i) => (
        p.type === 'd2'
          ? <D2Block key={i} code={p.value} />
          : p.type === 'mermaid'
            ? <MermaidBlock key={i} code={p.value} />
            : (p.value.trim() ? <Markdown key={i} text={p.value} /> : null)
      ))}
    </div>
  );
}
